import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { incidentalLoanService } from '../../services/incidentalLoanService'
import api from '../../services/api'
import Swal from 'sweetalert2'

function IncidentalLoanDisbursement() {
  const navigate = useNavigate()
  const [loans, setLoans] = useState([])
  const [selectedLoan, setSelectedLoan] = useState(null)
  const [settings, setSettings] = useState(null)
  const loanSelectRef = useRef(null)
  const [formData, setFormData] = useState({
    NoPinjaman: '',
    JumlahPencairan: 0,
    BiayaAdmin: 0,
    Keterangan: ''
  })
  const [loading, setLoading] = useState(false)
  const [showPrintModal, setShowPrintModal] = useState(false)
  const [printData, setPrintData] = useState(null)
  const [coopProfile, setCoopProfile] = useState(null)

  useEffect(() => {
    fetchApprovedLoans()
    fetchSettings()
    fetchCoopProfile()
  }, [])

  const fetchCoopProfile = async () => {
    try {
      const response = await api.get('/reports/profile-info')
      setCoopProfile(response.data.data?.coopProfile || response.data.data)
    } catch (error) {
      console.error('Error fetching coop profile:', error)
    }
  }

  const fetchSettings = async () => {
    try {
      const response = await incidentalLoanService.getSettings()
      if (response.data.data && response.data.data.length > 0) {
        setSettings(response.data.data[0])
      }
    } catch (error) {
      console.error('Error fetching settings:', error)
    }
  }

  const handleLoanChange = async (noPinjaman) => {
    const selected = loans.find(l => l.NoPinjaman === noPinjaman)
    setSelectedLoan(selected)

    if (selected) {
      const calculatedAdminFee = settings && settings.PersentaseAdmin
        ? (selected.JumlahPinjaman * settings.PersentaseAdmin) / 100
        : 0

      setFormData(prev => ({
        ...prev,
        NoPinjaman: noPinjaman,
        JumlahPencairan: selected.JumlahPinjaman || 0,
        BiayaAdmin: calculatedAdminFee
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        NoPinjaman: noPinjaman || '',
        JumlahPencairan: 0,
        BiayaAdmin: 0
      }))
    }
  }

  useEffect(() => {
    if (loanSelectRef.current && loans.length > 0) {
      if ($(loanSelectRef.current).data('select2')) {
        $(loanSelectRef.current).select2('destroy')
      }

      $(loanSelectRef.current).select2({
        placeholder: '-- Pilih Pinjaman --',
        allowClear: true,
        width: '100%'
      })

      $(loanSelectRef.current).on('change', function() {
        const selectedValue = $(this).val()
        handleLoanChange(selectedValue)
      })

      return () => {
        if (loanSelectRef.current) {
          $(loanSelectRef.current).off('change').select2('destroy')
        }
      }
    }
  }, [loans, settings])

  useEffect(() => {
    if (selectedLoan && settings && formData.NoPinjaman) {
      const calculatedAdminFee = settings.PersentaseAdmin 
        ? (selectedLoan.JumlahPinjaman * settings.PersentaseAdmin) / 100 
        : 0
      setFormData(prev => ({
        ...prev,
        BiayaAdmin: calculatedAdminFee
      }))
    }
  }, [settings, selectedLoan])

  const fetchApprovedLoans = async () => {
    try {
      const response = await incidentalLoanService.getAll({ status: 'Disetujui', limit: 1000 })
      setLoans(response.data.data || [])
    } catch (error) {
      console.error('Error fetching approved loans:', error)
      Swal.fire({
        icon: 'error',
        title: 'Gagal',
        text: 'Gagal mengambil data pinjaman'
      })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate before submission
    if (!formData.NoPinjaman) {
      Swal.fire({
        icon: 'error',
        title: 'Gagal',
        text: 'Silakan pilih pinjaman terlebih dahulu'
      })
      return
    }

    if (formData.JumlahPencairan <= 0) {
      Swal.fire({
        icon: 'error',
        title: 'Gagal',
        text: 'Jumlah pencairan harus lebih dari 0'
      })
      return
    }

    setLoading(true)

    try {
      const response = await incidentalLoanService.disburse(formData.NoPinjaman, {
        JumlahPencairan: formData.JumlahPencairan,
        BiayaAdmin: formData.BiayaAdmin,
        Keterangan: formData.Keterangan
      })
      
      const data = response.data.data
      
      setPrintData({
        NoPinjaman: data.NoPinjaman,
        NamaAnggota: data.namaAnggota,
        NBA: selectedLoan?.NBA,
        JumlahPencairan: data.JumlahPencairan,
        BiayaAdmin: data.BiayaAdmin,
        TotalDiterima: data.TotalDiterima,
        Keterangan: formData.Keterangan,
        Tanggal: new Date(data.TglPencairan).toLocaleDateString('id-ID'),
        NoJurnal: data.NoJurnal
      })
      
      setCoopProfile(prev => ({
        ...prev,
        NamaKoperasi: data.namaKoperasi
      }))
      
      setShowPrintModal(true)
      
      setFormData({
        NoPinjaman: '',
        JumlahPencairan: 0,
        BiayaAdmin: 0,
        Keterangan: ''
      })
      setSelectedLoan(null)
      if (loanSelectRef.current) {
        $(loanSelectRef.current).val(null).trigger('change')
      }
      fetchApprovedLoans()
      
      Swal.fire({
        icon: 'success',
        title: 'Berhasil',
        text: 'Pinjaman berhasil dicairkan'
      })
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Gagal',
        text: error.response?.data?.message || 'Gagal melakukan pencairan'
      })
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value || 0)
  }

  return (
    <div className="row">
      <div className="col-md-8">
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Pencairan Pinjaman Insidentil</h3>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="card-body">
              <div className="form-group">
                <label>Pilih Pinjaman *</label>
                <select
                  ref={loanSelectRef}
                  className="form-control"
                  value={formData.NoPinjaman}
                  onChange={() => {}}
                  required
                >
                  <option value="">-- Pilih Pinjaman --</option>
                  {Array.isArray(loans) && loans.map(l => (
                    <option key={l.NoPinjaman} value={l.NoPinjaman}>
                      {l.NoPinjaman} - {l.Nama} - {formatCurrency(l.JumlahPinjaman)}
                    </option>
                  ))}
                </select>
              </div>

              {selectedLoan && (
                <div className="alert alert-info">
                  <div><strong>NBA:</strong> {selectedLoan.NBA}</div>
                  <div><strong>Nama:</strong> {selectedLoan.Nama}</div>
                  <div><strong>Jumlah Pinjaman:</strong> {formatCurrency(selectedLoan.JumlahPinjaman)}</div>
                  <div><strong>Bunga:</strong> {selectedLoan.PersentaseBunga}% per tahun</div>
                  <div><strong>Jangka Waktu:</strong> {selectedLoan.JangkaWaktu} bulan</div>
                </div>
              )}

              <div className="form-group">
                <label>Jumlah Pencairan *</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.JumlahPencairan}
                  onChange={(e) => {
                    const newAmount = parseInt(e.target.value) || 0
                    const calculatedAdminFee = settings && settings.PersentaseAdmin 
                      ? (newAmount * settings.PersentaseAdmin) / 100 
                      : 0
                    setFormData({ 
                      ...formData, 
                      JumlahPencairan: newAmount,
                      BiayaAdmin: calculatedAdminFee
                    })
                  }}
                  required
                  min="0"
                  max={selectedLoan?.JumlahPinjaman || 999999999}
                />
                {selectedLoan && (
                  <small className="text-muted">
                    Maksimal: {formatCurrency(selectedLoan.JumlahPinjaman)}
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Biaya Admin</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.BiayaAdmin}
                  readOnly
                />
                {settings && settings.PersentaseAdmin && (
                  <small className="text-muted">
                    {settings.PersentaseAdmin}% dari jumlah pinjaman (Otomatis)
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Keterangan</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={formData.Keterangan}
                  onChange={(e) => setFormData({ ...formData, Keterangan: e.target.value })}
                />
              </div>
            </div>
            <div className="card-footer">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Memproses...
                  </>
                ) : (
                  <>
                    <i className="fas fa-money-bill-wave mr-2"></i>
                    Cairkan Pinjaman
                  </>
                )}
              </button>
              <button
                type="button"
                className="btn btn-default ml-2"
                onClick={() => navigate('/incidental-loans')}
              >
                Batal
              </button>
            </div>
          </form>
        </div>
      </div>

      {showPrintModal && printData && (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-success">
                <h5 className="modal-title">Bukti Pencairan Pinjaman Insidentil</h5>
                <button type="button" className="close text-white" onClick={() => setShowPrintModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <td><strong>No. Pinjaman</strong></td>
                        <td>{printData.NoPinjaman}</td>
                      </tr>
                      <tr>
                        <td><strong>No. Jurnal</strong></td>
                        <td>{printData.NoJurnal}</td>
                      </tr>
                      <tr>
                        <td><strong>Tanggal</strong></td>
                        <td>{printData.Tanggal}</td>
                      </tr>
                      <tr>
                        <td><strong>NBA</strong></td>
                        <td>{printData.NBA}</td>
                      </tr>
                      <tr>
                        <td><strong>Nama Anggota</strong></td>
                        <td>{printData.NamaAnggota}</td>
                      </tr>
                      <tr>
                        <td><strong>Jumlah Pencairan</strong></td>
                        <td className="text-right">{formatCurrency(printData.JumlahPencairan)}</td>
                      </tr>
                      <tr>
                        <td><strong>Biaya Admin</strong></td>
                        <td className="text-right">{formatCurrency(printData.BiayaAdmin)}</td>
                      </tr>
                      <tr>
                        <td><strong>Total Diterima</strong></td>
                        <td className="text-right font-weight-bold">{formatCurrency(printData.TotalDiterima)}</td>
                      </tr>
                      {printData.Keterangan && (
                        <tr>
                          <td><strong>Keterangan</strong></td>
                          <td>{printData.Keterangan}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPrintModal(false)}>
                  Tutup
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    const receiptContent = `
================================
  BUKTI PENCAIRAN PINJAMAN
      INSIDENTIL
================================
No Pinjaman: ${printData.NoPinjaman}
No Jurnal   : ${printData.NoJurnal}
Tanggal    : ${printData.Tanggal}
---------------------------------
DATA ANGGOTA
---------------------------------
NBA        : ${printData.NBA}
Nama       : ${printData.NamaAnggota}
---------------------------------
DETAIL PENCAIRAN
---------------------------------
Jumlah Pencairan: Rp ${parseFloat(printData.JumlahPencairan).toLocaleString('id-ID')}
Biaya Admin     : Rp ${parseFloat(printData.BiayaAdmin).toLocaleString('id-ID')}
---------------------------------
Total Diterima  : Rp ${parseFloat(printData.TotalDiterima).toLocaleString('id-ID')}
---------------------------------
${printData.Keterangan ? `KETERANGAN\n---------------------------------\n${printData.Keterangan}\n---------------------------------\n` : ''}
================================
  ${coopProfile?.NamaKoperasi || 'Koperasi'}
  ${coopProfile?.Alamat || ''}
================================
`
                    const printWindow = window.open('', '_blank')
                    printWindow.document.write(`
                      <html>
                        <head>
                          <title>Bukti Pencairan Pinjaman Insidentil</title>
                          <style>
                            body {
                              font-family: 'Courier New', monospace;
                              font-size: 12px;
                              margin: 0;
                              padding: 10px;
                              white-space: pre;
                              line-height: 1.2;
                            }
                          </style>
                        </head>
                        <body>${receiptContent}</body>
                      </html>
                    `)
                    printWindow.document.close()
                    printWindow.print()
                  }}
                >
                  <i className="fas fa-print mr-1"></i> Cetak
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default IncidentalLoanDisbursement
